import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { FileSignature, Check, Eraser, Download, Loader2, ShieldCheck } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SignLease() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasDrawn = useRef(false);

  useEffect(() => {
    axios.get(`${API}/sign/${token}`)
      .then((r) => { setInfo(r.data); setName(r.data.tenant_name || ""); if (r.data.status === "signed") setDone(true); })
      .catch(() => setErr("Lien de signature invalide ou expiré."));
  }, [token]);

  const pos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - rect.left) * (c.width / rect.width), y: (t.clientY - rect.top) * (c.height / rect.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const { x, y } = pos(e); const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.strokeStyle = "#121311";
    ctx.lineTo(x, y); ctx.stroke(); hasDrawn.current = true;
  };
  const end = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); hasDrawn.current = false; };

  const submit = async () => {
    if (!name.trim()) return alert("Veuillez saisir votre nom.");
    if (!hasDrawn.current) return alert("Veuillez dessiner votre signature.");
    setSubmitting(true);
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      await axios.post(`${API}/sign/${token}`, { signer_name: name.trim(), signature: dataUrl });
      setDone(true);
    } catch (e) {
      alert(e?.response?.data?.detail || "Erreur lors de la signature.");
    } finally { setSubmitting(false); }
  };

  const download = () => {
    const ep = info.doc_type === "solde" ? `solde-pdf` : info.doc_type === "edl_sortie" ? `edl-pdf` : `lease-pdf`;
    window.open(`${API}/sign/${token}/${ep}`, "_blank");
  };

  if (err) return <Shell><p className="text-center text-red-600">{err}</p></Shell>;
  if (!info) return <Shell><p className="text-center text-brand-green/50">Chargement...</p></Shell>;

  const isSolde = info.doc_type === "solde";
  const isEdl = info.doc_type === "edl_sortie";
  const pr = info.property || {};
  const sd = info.solde || {};
  const fmt = (n) => (n || 0).toLocaleString("fr-FR");
  const kicker = isSolde ? "Signature du solde de tout compte" : isEdl ? "Signature de l'état des lieux" : "Signature du bail";
  const heading = isSolde ? "Solde de tout compte" : isEdl ? "État des lieux de sortie" : "Contrat de location";

  return (
    <Shell>
      <div className="flex items-center gap-2 text-brand-limeDark mb-1"><FileSignature className="w-5 h-5" /><span className="text-sm font-semibold">360immoinvest · {kicker}</span></div>
      <h1 className="font-display text-2xl font-semibold text-brand-greenDeep mb-1">{heading}</h1>
      <p className="text-sm text-brand-green/60 mb-6">Bailleur : {info.owner_name || "—"}</p>

      {isSolde ? (
        <div className="rounded-2xl bg-brand-softer p-5 mb-6 text-sm space-y-1.5" data-testid="solde-summary">
          <p><span className="text-brand-green/50">Logement :</span> <b>{info.property_name}</b></p>
          <p><span className="text-brand-green/50">Locataire :</span> {info.tenant_name || "—"}</p>
          {sd.date_depart && <p><span className="text-brand-green/50">Date de départ :</span> {sd.date_depart}</p>}
          <p><span className="text-brand-green/50">Dépôt initial :</span> {fmt(sd.deposit)} €</p>
          <p><span className="text-brand-green/50">Total des retenues :</span> {fmt(sd.retenues_total)} €</p>
          <p className="pt-1 border-t border-brand-soft mt-1"><span className="text-brand-green/50">Solde restitué :</span> <b>{fmt(sd.restitue)} €</b></p>
        </div>
      ) : isEdl ? (
        <div className="rounded-2xl bg-brand-softer p-5 mb-6 text-sm space-y-1.5" data-testid="edl-summary">
          <p><span className="text-brand-green/50">Logement :</span> <b>{info.property_name}</b></p>
          <p><span className="text-brand-green/50">Locataire :</span> {info.tenant_name || "—"}</p>
          <p><span className="text-brand-green/50">Type :</span> État des lieux de sortie</p>
          <p><span className="text-brand-green/50">Photos jointes :</span> {info.photo_count || 0}</p>
        </div>
      ) : (
      <div className="rounded-2xl bg-brand-softer p-5 mb-6 text-sm space-y-1.5">
        <p><span className="text-brand-green/50">Logement :</span> <b>{pr.name || info.property_name}</b>{pr.type ? ` · ${pr.type}` : ""}</p>
        {pr.address && <p><span className="text-brand-green/50">Adresse :</span> {pr.address}</p>}
        <p><span className="text-brand-green/50">Locataire :</span> {info.tenant_name || "—"}</p>
        {info.start_date && <p><span className="text-brand-green/50">Prise d'effet :</span> {info.start_date}</p>}
        {info.duration && <p><span className="text-brand-green/50">Durée :</span> {info.duration}</p>}
        <p><span className="text-brand-green/50">Loyer CC :</span> <b>{((pr.rent || 0) + (pr.charges || 0)).toLocaleString("fr-FR")} €</b> · Dépôt : {(pr.deposit || 0).toLocaleString("fr-FR")} €</p>
      </div>
      )}

      {done ? (
        <div className="text-center py-6">
          <div className="w-16 h-16 rounded-full bg-brand-lime/30 flex items-center justify-center mx-auto mb-4"><Check className="w-8 h-8 text-brand-greenDeep" /></div>
          <h2 className="font-semibold text-xl text-brand-greenDeep">{isSolde ? "Solde signé électroniquement" : isEdl ? "État des lieux signé électroniquement" : "Bail signé électroniquement"}</h2>
          <p className="text-sm text-brand-green/60 mt-1 mb-5">{info.signed_at ? `Signé le ${info.signed_at}` : "Merci !"} Vous pouvez télécharger le document signé.</p>
          <button onClick={download} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-greenDeep text-brand-lime font-semibold" data-testid="download-signed-btn">
            <Download className="w-4 h-4" /> {isSolde ? "Télécharger le solde signé" : isEdl ? "Télécharger l'EDL signé" : "Télécharger le bail signé"}
          </button>
          <a href={`/verifier/${token}`} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-sm text-brand-limeDark hover:text-brand-greenDeep font-medium" data-testid="verify-seal-link">
            <ShieldCheck className="w-4 h-4" /> Vérifier l'authenticité du cachet
          </a>
        </div>
      ) : (
        <>
          <label className="block text-sm font-medium text-brand-green/70 mb-1.5">Votre nom complet</label>
          <input value={name} onChange={(e) => setName(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-brand-soft outline-none focus:border-brand-limeDark mb-4" data-testid="signer-name-input" />
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-sm font-medium text-brand-green/70">Votre signature</label>
            <button onClick={clear} className="text-xs inline-flex items-center gap-1 text-brand-green/60 hover:text-brand-greenDeep"><Eraser className="w-3.5 h-3.5" /> Effacer</button>
          </div>
          <canvas
            ref={canvasRef} width={560} height={180} data-testid="signature-canvas"
            className="w-full h-44 rounded-2xl border-2 border-dashed border-brand-soft bg-white touch-none cursor-crosshair"
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          />
          <p className="text-xs text-brand-green/45 mt-2 mb-5 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Signature électronique simple. En signant, vous acceptez les termes du document.</p>
          <button onClick={submit} disabled={submitting} className="w-full py-3 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="sign-submit-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4" />} {isSolde ? "Signer le solde" : isEdl ? "Signer l'état des lieux" : "Signer le bail"}
          </button>
        </>
      )}
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-brand-softer flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-white rounded-3xl border border-brand-soft shadow-xl p-8">{children}</div>
    </div>
  );
}
